import { memo } from 'react';
import NumberFlow from '@number-flow/react';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { getReadableViewershipPlatform } from 'root/src/shared/platforms/helpers/getReadablePlatform';
import { useIsAuthenticated } from 'root/src/client/v2/auth/hooks/useIsAuthenticated';
import { useMaxDaresByPlatforms } from 'root/src/client/logic/dropEvent/hooks/useMaxDaresByPlatforms';
import { useDaresLeftPerPlatform } from 'root/src/client/logic/dropEvent/hooks/useDaresLeftPerPlatform';
import { useDareRequirementMessages } from 'root/src/client/logic/dropEvent/hooks/useDareRequirementMessages';
import { DarePlatformIcon, PlatformIconColor } from 'root/src/client/web/base/CustomButton/PlatformIcon';
import { Popover, PopoverTrigger, PopoverContent } from 'root/src/client/v2/common/components/ui/popover';
import { cn } from 'root/src/client/v2/common/helpers/cn';
const chipClass = 'inline-flex items-center gap-1.5 rounded-full border border-solid border-neon-green/30 bg-neon-green/10 px-2.5 py-1 text-xs font-semibold text-neon-green';
// A drop with no breakpoints on a platform gives the creator every dare on it, which reaches us as
// Infinity rather than a count.
const formatLeft = (left) => (Number.isFinite(left) ? <NumberFlow value={left}/> : 'All');
// The redesign counterpart of the legacy DaresLeft chip: how many dares the creator can still accept,
// with the per-platform breakdown and the requirement copy behind an info popover instead of always on
// the page. Extra dares (earned from reviewing clips) reach the redesign via the drop payload rather than
// the userCampaignData record the legacy chip reads, and are folded into `maxDares` by useMaxDaresByPlatforms;
// the per-platform tiles below only cover viewership-based dares, so the extra ones get their own line.
export const DropDaresLeftBadge = memo(({ drop }) => {
    var _a, _b;
    const isAuthenticated = useIsAuthenticated();
    const maxDares = useMaxDaresByPlatforms({ fallbackDrop: drop });
    const daresLeft = Math.max(0, maxDares - ((_a = drop.creatorAcceptedDares) !== null && _a !== void 0 ? _a : 0));
    const daresLeftPerPlatform = useDaresLeftPerPlatform({ fallbackDrop: drop });
    const messages = useDareRequirementMessages({ fallbackDrop: drop });
    const extraDares = (_b = drop.extraDares) !== null && _b !== void 0 ? _b : 0;
    if (!isAuthenticated || maxDares <= 0) {
        return null;
    }
    const label = (<>
			<NumberFlow value={daresLeft}/>
			{daresLeft === 1 ? 'dare' : 'dares'} left for you
		</>);
    // Nothing to explain (no viewership platform overlaps the drop's dares, no requirement copy applies,
    // and no extra dares to call out), so the chip stays a plain label rather than offering an empty popover.
    if (!daresLeftPerPlatform.length && !messages.length && extraDares <= 0) {
        return <span className={chipClass}>{label}</span>;
    }
    return (<Popover>
			<PopoverTrigger asChild>
				<button type="button" aria-label="How many dares you can accept, and why" className={cn(chipClass, 'cursor-pointer transition-colors hover:bg-neon-green/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-neon-green/40')}>
					<InfoOutlinedIcon sx={{ fontSize: 14 }}/>
					{label}
				</button>
			</PopoverTrigger>
			<PopoverContent align="start" className="w-80 p-0">
				<div className="border-b border-solid border-white/[0.06] px-4 py-3">
					<p className="font-display text-sm font-semibold text-foreground">Dares left for you</p>
					<p className="text-xs text-muted-foreground">Your viewership sets how many dares you can accept on this drop.</p>
				</div>
				{daresLeftPerPlatform.length > 0 && (<ul className="flex gap-2 p-4">
						{daresLeftPerPlatform.map(({ platform, left }) => (<li key={platform} className="flex min-w-0 flex-1 flex-col items-center gap-1.5 rounded-lg border border-solid border-white/[0.06] bg-white/[0.02] px-2 py-2.5">
								<span className="flex h-6 w-6 items-center justify-center rounded-full bg-white">
									<DarePlatformIcon size="14" platform={platform} color={PlatformIconColor.Dark}/>
								</span>
								<p className="text-base font-bold text-foreground">{formatLeft(left)}</p>
								<p className="truncate text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
									{getReadableViewershipPlatform(platform)}
								</p>
							</li>))}
					</ul>)}
				{messages.length > 0 && (<ul className={cn('space-y-2 px-4 py-3', daresLeftPerPlatform.length > 0 && 'border-t border-solid border-white/[0.06]')}>
						{messages.map(message => (<li key={message} className="text-xs leading-relaxed text-muted-foreground">{message}</li>))}
					</ul>)}
				{extraDares > 0 && (<p className={cn('px-4 py-3 text-xs leading-relaxed text-neon-teal', (daresLeftPerPlatform.length > 0 || messages.length > 0) && 'border-t border-solid border-white/[0.06]')}>
						You also have <span className="font-bold">{extraDares} extra dare{extraDares === 1 ? '' : 's'}</span> from
						reviewing clips, usable across any platform.
					</p>)}
			</PopoverContent>
		</Popover>);
});
